// Elegant themed visual blocks for sections without authentic photographs.
// Each variant renders a rich SVG + gradient composition tailored to the topic.
// No fake photographs, no stock images — pure branded graphic design.
import React from "react";

const PALETTES = {
  ocean:    { from: "#0F2C59", to: "#163d7a", accent: "#14B8A6", ink: "#ffffff" },
  teal:     { from: "#0E4C57", to: "#0F2C59", accent: "#5EEAD4", ink: "#ffffff" },
  sunset:   { from: "#0F2C59", to: "#D97757", accent: "#FDBA74", ink: "#ffffff" },
  clinical: { from: "#0F2C59", to: "#1E3A8A", accent: "#38BDF8", ink: "#ffffff" },
  aqua:     { from: "#155E75", to: "#0F2C59", accent: "#22D3EE", ink: "#ffffff" },
};

function ShellFrame({ aspect, className, palette, children, testid }) {
  const p = PALETTES[palette] || PALETTES.ocean;
  return (
    <div
      className={`relative rounded-2xl overflow-hidden w-full ${aspect} ${className}`}
      style={{ background: `linear-gradient(135deg, ${p.from} 0%, ${p.to} 100%)`, color: p.ink }}
      data-testid={testid}
      aria-hidden="true"
    >
      {/* subtle grid overlay */}
      <div className="absolute inset-0 opacity-[0.10]"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }} />
      {/* soft radial highlights */}
      <div className="absolute -top-24 -left-16 w-[70%] h-[70%] rounded-full blur-3xl opacity-25"
           style={{ background: `radial-gradient(circle at center, ${p.accent} 0%, transparent 65%)` }} />
      <div className="absolute -bottom-24 -right-16 w-[70%] h-[70%] rounded-full blur-3xl opacity-15"
           style={{ background: `radial-gradient(circle at center, ${p.accent} 0%, transparent 65%)` }} />
      {children}
    </div>
  );
}

/* ---- Individual scene SVGs -----------------------------------------*/

function SceneClinic({ p }) {
  return (
    <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="cg1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={p.accent} stopOpacity="0.7"/><stop offset="1" stopColor={p.accent} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {/* stylized clinic silhouette */}
      <path d="M40 220 L40 130 L110 130 L110 90 L200 90 L200 130 L360 130 L360 220 Z"
            fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
      <rect x="60" y="150" width="30" height="40" rx="3" fill="rgba(255,255,255,0.10)" />
      <rect x="120" y="110" width="24" height="30" rx="3" fill="rgba(255,255,255,0.10)" />
      <rect x="160" y="110" width="24" height="30" rx="3" fill="rgba(255,255,255,0.10)" />
      <rect x="220" y="150" width="120" height="40" rx="3" fill="rgba(255,255,255,0.08)" />
      {/* cross medical symbol */}
      <g transform="translate(300,55)">
        <rect x="-14" y="-4" width="28" height="8" rx="2" fill={p.accent} opacity="0.9"/>
        <rect x="-4" y="-14" width="8" height="28" rx="2" fill={p.accent} opacity="0.9"/>
      </g>
      <circle cx="70" cy="80" r="3" fill="url(#cg1)"/>
      <circle cx="240" cy="50" r="2" fill={p.accent} opacity="0.7"/>
    </svg>
  );
}

function SceneTooth({ p, sub = "" }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,150)">
        <path d="M-60 -40 C -70 -80 -40 -100 -10 -95 C 5 -100 20 -100 35 -95 C 65 -100 90 -70 75 -30 C 65 0 55 30 45 70 C 40 90 15 95 8 70 C 3 55 -3 55 -8 70 C -15 95 -40 90 -45 70 C -55 30 -60 0 -60 -40 Z"
              fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5"/>
        <path d="M-30 -50 C -20 -60 20 -60 30 -50" fill="none" stroke={p.accent} strokeWidth="2" opacity="0.9"/>
        <circle cx="0" cy="-25" r="4" fill={p.accent} opacity="0.9"/>
      </g>
      {sub && (
        <text x="150" y="270" textAnchor="middle" fill="rgba(255,255,255,0.55)"
              fontFamily="Manrope, sans-serif" fontSize="11" letterSpacing="4">{sub}</text>
      )}
    </svg>
  );
}

function SceneImplant({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,90)">
        {/* crown */}
        <path d="M-32 0 C -25 -25 25 -25 32 0 L 24 20 L -24 20 Z"
              fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5"/>
        {/* abutment */}
        <rect x="-8" y="22" width="16" height="14" fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2"/>
        {/* implant screw */}
        <g transform="translate(0,38)" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" fill="rgba(255,255,255,0.10)">
          <path d="M-12 0 L 12 0 L 10 10 L -10 10 Z"/>
          <path d="M-10 12 L 10 12 L 8 24 L -8 24 Z"/>
          <path d="M-8 26 L 8 26 L 6 40 L -6 40 Z"/>
          <path d="M-6 42 L 6 42 L 4 56 L -4 56 Z"/>
          <path d="M-4 58 L 4 58 L 2 72 L -2 72 Z"/>
          <path d="M-2 74 L 2 74 L 0 88 Z"/>
        </g>
      </g>
      {/* ground / gum line */}
      <path d="M0 210 Q 150 190 300 210 L 300 300 L 0 300 Z" fill={p.accent} opacity="0.15"/>
      <line x1="0" y1="210" x2="300" y2="210" stroke={p.accent} strokeWidth="1" opacity="0.6" strokeDasharray="4 4"/>
    </svg>
  );
}

function SceneScanner({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      {/* 3D scan cube */}
      <g transform="translate(150,150)" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" fill="none">
        <polygon points="0,-70 60,-40 60,40 0,70 -60,40 -60,-40" />
        <polygon points="0,-70 0,0 60,-40" />
        <polygon points="0,-70 0,0 -60,-40" />
        <line x1="0" y1="0" x2="0" y2="70"/>
        <line x1="0" y1="0" x2="-60" y2="40"/>
        <line x1="0" y1="0" x2="60" y2="40"/>
      </g>
      {/* scan lines */}
      <line x1="30" y1="90" x2="270" y2="90" stroke={p.accent} strokeWidth="1" opacity="0.6"/>
      <line x1="30" y1="150" x2="270" y2="150" stroke={p.accent} strokeWidth="1" opacity="0.8"/>
      <line x1="30" y1="210" x2="270" y2="210" stroke={p.accent} strokeWidth="1" opacity="0.4"/>
      <circle cx="240" cy="60" r="3" fill={p.accent}/>
    </svg>
  );
}

function SceneMicroscope({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,155)" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="rgba(255,255,255,0.06)">
        <rect x="-60" y="70" width="120" height="14" rx="3"/>
        <rect x="-8" y="46" width="16" height="26"/>
        <circle cx="0" cy="30" r="14"/>
        <circle cx="0" cy="30" r="6" fill={p.accent} opacity="0.7"/>
        <path d="M0 16 L 0 -30 L 30 -30 L 30 -60"/>
        <circle cx="30" cy="-64" r="10" fill="rgba(255,255,255,0.10)"/>
      </g>
      {/* magnification dots */}
      {[[80,60],[220,80],[100,40],[220,180],[70,180]].map(([x,y],i)=>(
        <circle key={i} cx={x} cy={y} r={i===2?2.5:1.8} fill={p.accent} opacity="0.7"/>
      ))}
    </svg>
  );
}

function SceneLaser({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,150)">
        <rect x="-70" y="-8" width="90" height="16" rx="8" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.45)" strokeWidth="1.2"/>
        <path d="M20 0 L 130 0" stroke={p.accent} strokeWidth="3" opacity="0.9"/>
        <path d="M20 -10 L 130 -10" stroke={p.accent} strokeWidth="1" opacity="0.5"/>
        <path d="M20 10 L 130 10" stroke={p.accent} strokeWidth="1" opacity="0.5"/>
        <circle cx="130" cy="0" r="14" fill={p.accent} opacity="0.35"/>
        <circle cx="130" cy="0" r="6" fill={p.accent}/>
      </g>
    </svg>
  );
}

function SceneEMS({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      {/* particles */}
      {Array.from({ length: 22 }).map((_, i) => {
        const x = 30 + (i * 47) % 240;
        const y = 40 + ((i * 31) % 200);
        const r = 1.5 + ((i * 7) % 4);
        return <circle key={i} cx={x} cy={y} r={r} fill={p.accent} opacity={0.35 + ((i * 11) % 40) / 100}/>;
      })}
      {/* airflow curves */}
      <g stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" fill="none" strokeLinecap="round">
        <path d="M40 220 C 100 150 200 250 260 170"/>
        <path d="M40 240 C 100 180 200 270 260 200" opacity="0.5"/>
      </g>
    </svg>
  );
}

function SceneModjaw({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,150)">
        {/* upper jaw arch */}
        <path d="M-90 -20 Q 0 -90 90 -20" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
        {/* lower jaw arch */}
        <path d="M-90 20 Q 0 90 90 20" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
        {/* motion arcs */}
        {[0.4, 0.6, 0.8].map((o,i) => (
          <path key={i} d={`M-${90-i*12} 0 Q 0 ${60-i*10} ${90-i*12} 0`} fill="none" stroke={p.accent} strokeWidth="1.2" opacity={o} strokeDasharray="3 3"/>
        ))}
      </g>
    </svg>
  );
}

function SceneOrtho({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,150)">
        <path d="M-110 0 Q 0 -80 110 0" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2"/>
        {[-90,-60,-30,0,30,60,90].map((x,i) => (
          <g key={i}>
            <rect x={x-8} y={-38 + Math.abs(x)/6} width="16" height="18" rx="2" fill="rgba(255,255,255,0.10)" stroke="rgba(255,255,255,0.55)" strokeWidth="1"/>
            <circle cx={x} cy={-29 + Math.abs(x)/6} r="2.4" fill={p.accent}/>
          </g>
        ))}
      </g>
    </svg>
  );
}

function SceneUrgent({ p }) {
  return (
    <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150,150)">
        <circle r="70" fill="none" stroke={p.accent} strokeWidth="2" opacity="0.85"/>
        <circle r="88" fill="none" stroke={p.accent} strokeWidth="1" opacity="0.4"/>
        <circle r="108" fill="none" stroke={p.accent} strokeWidth="1" opacity="0.2"/>
        {/* clock */}
        <line x1="0" y1="0" x2="0" y2="-42" stroke="rgba(255,255,255,0.8)" strokeWidth="2"/>
        <line x1="0" y1="0" x2="30" y2="0" stroke="rgba(255,255,255,0.6)" strokeWidth="2"/>
        <circle r="4" fill={p.accent}/>
      </g>
    </svg>
  );
}

function SceneDiaspora({ p }) {
  return (
    <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      {/* dotted world map suggestion */}
      {Array.from({length: 90}).map((_,i)=>{
        const x = (i * 23) % 380 + 10;
        const y = (Math.sin(i * 0.7) * 60 + 150);
        return <circle key={i} cx={x} cy={y} r={0.9} fill="rgba(255,255,255,0.45)"/>;
      })}
      {/* travel arc */}
      <path d="M50 240 Q 200 40 350 200" fill="none" stroke={p.accent} strokeWidth="2" strokeDasharray="4 5"/>
      <circle cx="50" cy="240" r="6" fill={p.accent}/>
      <circle cx="350" cy="200" r="6" fill={p.accent}/>
      {/* plane */}
      <g transform="translate(200,90) rotate(20)" fill="rgba(255,255,255,0.9)">
        <path d="M-14 0 L 14 -4 L 14 4 Z"/>
        <path d="M-6 -3 L -12 -12 L -8 -12 L -3 -3 Z"/>
        <path d="M-6 3 L -12 12 L -8 12 L -3 3 Z"/>
      </g>
    </svg>
  );
}

function SceneMap({ p }) {
  return (
    <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      {/* roads */}
      <g stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" fill="none">
        <path d="M0 90 Q 120 100 220 70 Q 320 40 400 90"/>
        <path d="M0 180 Q 140 200 260 170 Q 340 155 400 180"/>
        <path d="M60 0 L 90 300"/>
        <path d="M240 0 L 210 300"/>
      </g>
      {/* map grid */}
      <g stroke="rgba(255,255,255,0.10)" strokeWidth="1" fill="none">
        {Array.from({length:8}).map((_,i)=><line key={"h"+i} x1="0" x2="400" y1={i*40} y2={i*40}/>)}
        {Array.from({length:11}).map((_,i)=><line key={"v"+i} x1={i*40} x2={i*40} y1="0" y2="300"/>)}
      </g>
      {/* location pin */}
      <g transform="translate(200,140)">
        <circle r="26" fill={p.accent} opacity="0.2"/>
        <circle r="16" fill={p.accent} opacity="0.4"/>
        <path d="M0 -12 C -10 -12 -10 0 0 10 C 10 0 10 -12 0 -12 Z" fill={p.accent}/>
        <circle cy="-4" r="4" fill="#fff"/>
      </g>
    </svg>
  );
}

const VARIANTS = {
  clinic:    { scene: SceneClinic,    palette: "ocean",    label: "Clinica Denti Service · Bacău" },
  crown:     { scene: SceneTooth,     palette: "aqua",     label: "Coroană CEREC · flux digital" },
  implant:   { scene: SceneImplant,   palette: "clinical", label: "Implant dentar · planificare digitală" },
  scanner:   { scene: SceneScanner,   palette: "teal",     label: "Radiologie digitală · CBCT AXEOS" },
  microscope:{ scene: SceneMicroscope,palette: "clinical", label: "Endodonție la microscop" },
  laser:     { scene: SceneLaser,     palette: "sunset",   label: "Terapie laser · Biolase" },
  ems:       { scene: SceneEMS,       palette: "aqua",     label: "Igienizare EMS · Guided Biofilm Therapy" },
  modjaw:    { scene: SceneModjaw,    palette: "teal",     label: "MODJAW 4D · analiza mișcărilor" },
  orthodontics: { scene: SceneOrtho,  palette: "ocean",    label: "Ortodonție · aliniere personalizată" },
  urgent:    { scene: SceneUrgent,    palette: "sunset",   label: "Urgențe stomatologice" },
  diaspora:  { scene: SceneDiaspora,  palette: "ocean",    label: "Pacienți din străinătate" },
  map:       { scene: SceneMap,       palette: "clinical", label: "Strada Ștefan cel Mare nr. 34, Bacău" },
  team:      { scene: SceneClinic,    palette: "ocean",    label: "Echipa Denti Service" },
  aesthetic: { scene: SceneTooth,     palette: "sunset",   label: "Estetică dentară" },
  bruxism:   { scene: SceneModjaw,    palette: "sunset",   label: "Bruxism și uzură dentară" },
  digital:   { scene: SceneScanner,   palette: "aqua",     label: "Diagnostic digital integrat" },
  prosthetics: { scene: SceneTooth,   palette: "ocean",    label: "Protetică dentară" },
  gum:       { scene: SceneEMS,       palette: "teal",     label: "Parodontologie" },
};

export default function BrandedVisual({ variant = "clinic", aspect = "aspect-[4/3]", className = "", label }) {
  const cfg = VARIANTS[variant] || VARIANTS.clinic;
  const p = PALETTES[cfg.palette];
  const Scene = cfg.scene;
  const caption = label || cfg.label;
  return (
    <ShellFrame aspect={aspect} className={className} palette={cfg.palette} testid={`branded-${variant}`}>
      <Scene p={p} />
      <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
        <div className="inline-flex items-center gap-2 self-start rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold"
             style={{ background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.22)", backdropFilter: "blur(6px)" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.accent }}></span>
          Denti Service · din 1992
        </div>
        <div className="mt-3 font-display font-extrabold text-lg md:text-xl leading-tight max-w-[95%]" style={{ color: p.ink }}>
          {caption}
        </div>
      </div>
    </ShellFrame>
  );
}
